/**
 * SummaryActions — 4 post-summary transformation buttons:
 *   - Explique-moi (vulgarize for a target audience)
 *   - Reformuler (alternative phrasing / anti-plagiarism)
 *   - Traduire (12 languages)
 *   - Polir (fix orthography + fluidity)
 *
 * All actions hit POST /api/summarize/transform. The transformed text is
 * displayed inline below the original summary with Copy / Replace / Dismiss
 * controls. Transforms do NOT count against the lifetime free-summary quota.
 */
import { useL } from "@/lib/useL";
import React, { useState } from "react";
import { ACTION_HELP } from "@/components/actionHelp";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Baby,
  RefreshCcw,
  Languages,
  Sparkles,
  Loader2,
  Copy,
  Check,
  X,
  ArrowDownUp,
  UserPlus,
  Lock,
  Wand2,
} from "lucide-react";
import { InfoTip } from "@/components/InfoTip";
import { PaidOnlyNote } from "@/components/PaidOnlyNote";
import { useBilling } from "@/contexts/BillingContext";
import { ActionBarButton, ActionBarHeader, optionCls, menuItemCls } from "@/components/ActionBar";
import { api } from "@/lib/api";
import { renderSummary } from "@/lib/renderSummary";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const EXPLAIN_AUDIENCES = [
  { key: "enfant",   label: "Enfant (10 ans)" },
  { key: "ado",      label: "Ado (15 ans)" },
  { key: "debutant", label: "Débutant complet" },
  { key: "etranger", label: "Non-francophone (B1)" },
];

const REFORMULATE_STYLES = [
  { key: "alt",     label: "Autre tournure" },
  { key: "concise", label: "Plus concis" },
  { key: "formel",  label: "Registre formel" },
  { key: "simple",  label: "Registre simple" },
];

const TRANSLATE_LANGS = [
  { key: "en", label: "English" },
  { key: "es", label: "Español" },
  { key: "de", label: "Deutsch" },
  { key: "it", label: "Italiano" },
  { key: "pt", label: "Português" },
  { key: "nl", label: "Nederlands" },
  { key: "ar", label: "العربية" },
  { key: "zh", label: "中文" },
  { key: "ja", label: "日本語" },
  { key: "ru", label: "Русский" },
  { key: "ko", label: "한국어" },
  { key: "fr", label: "Français" },
];

const EN_SUB = { enfant: "Child (10 years old)", ado: "Teen (15 years old)", debutant: "Complete beginner", etranger: "Non-native reader (B1)", alt: "Different wording", concise: "More concise", formel: "Formal register", simple: "Simple register" };
const enList = (list) => list.map((x) => ({ ...x, label: EN_SUB[x.key] || x.label }));
const ACTION_LABELS_EN = { explain: "Simplified", reformulate: "Rephrased", translate: "Translation", polish: "Polished version" };

const ACTION_LABELS = {
  explain: "Vulgarisation",
  reformulate: "Reformulation",
  translate: "Traduction",
  polish: "Version polie",
};

export const SummaryActions = ({ summary, model = "claude-sonnet-4-5", onReplace }) => {
  const L = useL();
  const [busy, setBusy] = useState(null); // "explain" | "reformulate" | "translate" | "polish"
  const [transformed, setTransformed] = useState(null); // { action, target, text }
  const [copied, setCopied] = useState(false);
  const [anonLocked, setAnonLocked] = useState(false); // 402 from backend → inline CTA
  const [open, setOpen] = useState(false);
  const { canUseExtras } = useBilling();
  const [selected, setSelected] = useState(null); // { action, target, label }

  const run = async (action, target, label) => {
    if (busy || anonLocked || !canUseExtras) return;
    setBusy(action);
    setSelected({ action, target, label });
    try {
      const { data } = await api.post("/summarize/transform", {
        summary,
        action,
        target,
        model,
      });
      setTransformed({ action, target, text: data.result });
      setCopied(false);
      // Scroll result into view for mobile ergonomics
      requestAnimationFrame(() => {
        document
          .querySelector('[data-testid="summary-transform-result"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      const status = err?.response?.status;
      // 402 = anonymous quota exhausted. Flip the panel into "unlock" mode
      // instead of showing a stacked toast — the inline CTA converts better.
      if (status === 402) {
        setAnonLocked(true);
        setTransformed(null);
        requestAnimationFrame(() => {
          document
            .querySelector('[data-testid="summary-actions-anon-cta"]')
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
        return;
      }
      const detail = err?.response?.data?.detail || err?.message || L("Transformation échouée", "Transformation failed");
      toast.error(detail);
    } finally {
      setBusy(null);
    }
  };

  const copy = async () => {
    if (!transformed) return;
    try {
      await navigator.clipboard.writeText(transformed.text);
      setCopied(true);
      toast.success(L("Copié dans le presse-papier", "Copied to clipboard"));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(L("Impossible de copier", "Could not copy"));
    }
  };

  const replace = () => {
    if (!transformed || !onReplace) return;
    onReplace(transformed.text);
    toast.success(L("Résumé remplacé par la version transformée", "Summary replaced by the transformed version"));
    setTransformed(null);
  };

  const dismiss = () => setTransformed(null);

  const isSel = (action, target) => selected?.action === action && (target === undefined || selected?.target === target);

  const renderIcon = (action) => {
    if (busy === action) return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    if (action === "explain") return <Baby className="w-3.5 h-3.5" />;
    if (action === "reformulate") return <RefreshCcw className="w-3.5 h-3.5" />;
    if (action === "translate") return <Languages className="w-3.5 h-3.5" />;
    if (action === "polish") return <Sparkles className="w-3.5 h-3.5" />;
    return null;
  };

  const menu = (action, items, label, tip, extraCls = "") => (
    <div className="flex items-center gap-1.5">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" disabled={!!busy || !canUseExtras} data-testid={`action-${action}-btn`} className={optionCls(isSel(action))} data-tip={tip}>
            {renderIcon(action)} {label}
            {isSel(action) && selected.label ? <span className="normal-case tracking-normal">· {selected.label}</span> : null}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className={`min-w-[200px] ${extraCls}`}>
          {items.map((a) => (
            <DropdownMenuItem
              key={a.key}
              onSelect={() => run(action, a.key, a.label)}
              data-testid={`action-${action}-${a.key}`}
              className={menuItemCls(isSel(action, a.key))}
            >
              {a.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div className="mt-8 max-w-3xl" data-testid="summary-actions">
      {!open ? (
        <ActionBarButton
          icon={Wand2}
          label={L("Actions sur le résumé", "Summary actions")}
          onClick={() => setOpen(true)}
          testid="summary-actions-open-btn"
          help={ACTION_HELP.summaryActions}
          tip={L("Actions sur le résumé : vulgariser, reformuler, traduire en 12 langues ou corriger le résumé obtenu, en 1 clic.", "Summary actions: simplify, rephrase, translate into 12 languages or polish the summary, in 1 click.")}
        />
      ) : (
        <div className="border border-border bg-card" data-testid="summary-actions-panel">
          <ActionBarHeader icon={Wand2} label={L("Actions sur le résumé", "Summary actions")} onCollapse={() => setOpen(false)} testid="summary-actions-collapse-btn" />
          <div className="p-4">
            {!canUseExtras && <PaidOnlyNote testid="summary-actions-paid-note" />}
            <div className="flex flex-wrap items-center justify-center gap-2">
              {menu("explain", L(EXPLAIN_AUDIENCES, enList(EXPLAIN_AUDIENCES)), L("Explique-moi", "Explain it"), L("Explique-moi : vulgarise le résumé pour un public précis (enfant, ado, débutant, non-francophone).", "Explain it: simplifies the summary for a specific audience (child, teen, beginner, non-native reader)."))}
              <InfoTip testid="action-explain-info" content={L(<><b>Explique-moi</b> : rend le résumé compréhensible pour un enfant de 10 ans, un ado de 15 ans, un débutant complet ou un lecteur non francophone (niveau B1).</>, <><b>Explain it</b>: makes the summary understandable for a 10-year-old, a 15-year-old, a complete beginner or a non-native reader (B1 level).</>)} />
              {menu("reformulate", L(REFORMULATE_STYLES, enList(REFORMULATE_STYLES)), L("Reformuler", "Rephrase"), L("Reformuler : réécrit le résumé dans un autre style, sans changer le sens ni les faits.", "Rephrase: rewrites the summary in another style, without changing the meaning or facts."))}
              <InfoTip testid="action-reformulate-info" content={L(<><b>Reformuler</b> : autre tournure, plus concis, registre formel ou registre simple. Les faits, chiffres et noms restent identiques.</>, <><b>Rephrase</b>: different wording, more concise, formal or simple register. Facts, figures and names stay the same.</>)} />
              {menu("translate", TRANSLATE_LANGS, L("Traduire", "Translate"), L("Traduire : traduit le résumé dans l'une des 12 langues proposées.", "Translate: translates the summary into one of the 12 languages offered."), "max-h-[320px] overflow-y-auto")}
              <InfoTip testid="action-translate-info" content={L(<><b>Traduire</b> : traduction naturelle (pas mot à mot) en 12 langues, en gardant titres et listes.</>, <><b>Translate</b>: natural translation (not word for word) into 12 languages, keeping headings and lists.</>)} />
              <button
                type="button"
                disabled={!!busy || !canUseExtras}
                onClick={() => run("polish", undefined, "")}
                data-testid="action-polish-btn"
                className={optionCls(isSel("polish"))}
                data-tip={L("Polir : corrige orthographe, grammaire, accents et fluidifie le texte, sans changer le sens ni la longueur.", "Polish: fixes spelling, grammar and accents and smooths the text, without changing the meaning or length.")}
              >
                {renderIcon("polish")} {L("Polir", "Polish")}
              </button>
              <InfoTip testid="action-polish-info" content={L(<><b>Polir</b> : corrige l'orthographe, la grammaire, les accents et la ponctuation, et rend les phrases plus fluides. Action directe, sans menu.</>, <><b>Polish</b>: fixes spelling, grammar, accents and punctuation, and makes sentences flow better. Direct action, no menu.</>)} />
            </div>
            <div className="mt-3 text-center font-['IBM_Plex_Sans'] text-[11px] text-muted-foreground leading-relaxed">
              {L(<><b>Explique-moi</b> vulgarise · <b>Reformuler</b> change le style · <b>Traduire</b> en 12 langues · <b>Polir</b> corrige orthographe & fluidité</>,
                 <><b>Explain it</b> simplifies · <b>Rephrase</b> changes the style · <b>Translate</b> into 12 languages · <b>Polish</b> fixes spelling & flow</>)}
            </div>
          </div>
        </div>
      )}

      {/* Anonymous paywall — after the 1st free transform, surface a non-blocking
          inline CTA pushing the signup. Preserves the visibility of the buttons
          (so the user sees what else is possible) but reroutes clicks. */}
      {anonLocked && !transformed && (
        <div
          data-testid="summary-actions-anon-cta"
          className="mt-6 border border-[#002FA7] bg-[#002FA7]/[0.04]"
        >
          <div className="px-4 py-3 border-b border-[#002FA7]/30 flex items-center gap-2 bg-[#002FA7]/10">
            <Lock className="w-4 h-4 text-[#002FA7]" />
            <div className="sux-marker text-[#002FA7]">{L("1/1 action gratuite utilisée", "1/1 free action used")}</div>
          </div>
          <div className="p-4 flex flex-col gap-3">
            <div className="font-['IBM_Plex_Sans'] text-sm leading-relaxed">
              {L(<><b>Vous avez essayé une transformation gratuite.</b><br />Créez un compte gratuit en 15 secondes pour débloquer les 4 actions (avec crédits) et garder votre <b>historique</b>. Pas de carte demandée.</>,
                 <><b>You tried a free transformation.</b><br />Create a free account in 15 seconds to unlock the 4 actions (with credits) and keep your <b>history</b>. No card required.</>)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/signup"
                data-testid="transform-anon-signup-btn"
                className="font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 bg-[#002FA7] text-white hover:bg-[#001a66] transition-colors flex items-center gap-2"
              >
                <UserPlus className="w-3.5 h-3.5" />
                {L("Créer un compte gratuit", "Create a free account")}
              </Link>
              <Link
                to="/login"
                data-testid="transform-anon-login-btn"
                className="font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
              >
                {L("J'ai déjà un compte", "I already have an account")}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Result panel */}
      {transformed && (
        <div
          data-testid="summary-transform-result"
          className="mt-6 border border-[#002FA7]/30 bg-[#002FA7]/[0.03]"
        >
          <div className="px-4 py-2 border-b border-[#002FA7]/30 flex items-center justify-between flex-wrap gap-2 bg-[#002FA7]/5">
            <div className="sux-marker text-[#002FA7]">
              {L(ACTION_LABELS, ACTION_LABELS_EN)[transformed.action]}
              {transformed.target ? ` · ${transformed.target}` : ""}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={copy}
                data-testid="transform-copy-btn"
                className="font-mono uppercase tracking-[0.14em] text-[10px] px-2.5 py-1.5 border border-[#002FA7]/40 text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-1.5"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? L("Copié", "Copied") : L("Copier", "Copy")}
              </button>
              {onReplace && (
                <button
                  type="button"
                  onClick={replace}
                  data-testid="transform-replace-btn"
                  className="font-mono uppercase tracking-[0.14em] text-[10px] px-2.5 py-1.5 border border-[#002FA7]/40 text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-1.5"
                  title={L("Remplacer le résumé par cette version", "Replace the summary with this version")}
                >
                  <ArrowDownUp className="w-3 h-3" /> {L("Remplacer", "Replace")}
                </button>
              )}
              <button
                type="button"
                onClick={dismiss}
                data-testid="transform-dismiss-btn"
                aria-label={L("Fermer", "Close")}
                className="font-mono uppercase tracking-[0.14em] text-[10px] px-2 py-1.5 border border-[#002FA7]/40 text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
          <article
            className="summary-content notranslate px-4 py-4"
            translate="no"
            dangerouslySetInnerHTML={{ __html: renderSummary(transformed.text) }}
          />
        </div>
      )}
    </div>
  );
};

export default SummaryActions;
